import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Paper, Tab, Tabs } from '@material-ui/core';
import { TabContext, TabPanel } from '@material-ui/lab';
import { applyUkgUserTimeZoneTransformation, convertNullableDateTimeToUkgUserDateTime } from '@commonClient2/services/ukgDateTime';
import BaseInfo from './BaseInfo';
import ScheduleSelection from './ScheduleSelection';
import EmployeeSelection from './EmployeeSelection';
import SchedulePeriod from './SchedulePeriod';
import BidSummary from './BidSummary';
import BidDashboard from './BidDashboard';
import { loadWorkplans } from '../store/appData';
import useSelectTab from './hooks/useSelectTab';
import {
    UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
    UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
    getSequentialEmployeeScheduleDraft,
} from '../store/bidProfileDraft';
import {
    BidProfileEditTab,
    BidProfileStatus,
    BidProcessType,
    ScheduleSource,
} from '../../Enums';
import BidEditHeader from './Header';
import './BidProfileEdit.scss';

/** @param {number} index */
const a11yProps = (index) => ({ id: `bid-profile-tab-${index}`, 'aria-controls': `bid-profile-tabpanel-${index}` });

function BidProfileEdit() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit' });
    const { selectedTab, setSelectedTab } = useSelectTab(BidProfileEditTab.Form);

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const {
        bidProfileDraft, bidProfile,
    } = useSelector((state) => state);

    const bidProfileDraftRef = useRef(bidProfileDraft);

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile | undefined}
     */
    let profile;
    let isDraft = false;
    let hasCreationAccess = false;
    let hasManagementAccess = false;
    let hasViewAccess = false;

    let handleIncrementSubmissionStartDate = () => { };
    if (bidProfileDraft.id !== '') {
        isDraft = true;
        profile = bidProfileDraft;
        hasCreationAccess = true;
        hasManagementAccess = false;
        hasViewAccess = false;
        bidProfileDraftRef.current = bidProfileDraft;
        handleIncrementSubmissionStartDate = () => {
            const start = applyUkgUserTimeZoneTransformation(new Date());
            const incrementStartDate = new Date(
                start.getFullYear(),
                start.getMonth(),
                start.getDate(),
                start.getHours(),
                (Math.ceil((start.getMinutes() * 60 + start.getSeconds()) / 300) * 300) / 60,
                0,
                0,
            );            
            
            if (incrementStartDate.getTime() <= bidProfileDraftRef.current?.originalSchedule.submissionPeriodStartDate?.getTime()) {
                return;
            }

            if (bidProfileDraftRef.current.originalBidProfileInformation.bidProcessType === BidProcessType.Sequential) {
                dispatch(getSequentialEmployeeScheduleDraft(
                    bidProfileDraft.draftId,
                    {
                        employees: bidProfileDraftRef.current.originalEmployees,
                        schedule: {
                            ...bidProfileDraftRef.current.originalSchedule,
                            submissionPeriodStartDate: incrementStartDate,
                        },
                    },
                )).then((promise) => {
                    dispatch({
                        type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                        payload: {
                            submissionPeriodStartDate: incrementStartDate,
                            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(promise.payload.submissionPeriodEndDate)
                                || bidProfileDraftRef.current?.originalSchedule.submissionPeriodEndDate,
                            sequentialSchedule: promise.payload.sequentialSchedule || {},
                        },
                    });
                }).catch((error) => {
                    dispatch({
                        type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                        payload: {
                            submissionPeriodStartDate: incrementStartDate,
                        },
                    });
                });
            } else {
                dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload: {
                        submissionPeriodStartDate: incrementStartDate,
                    },
                });
                dispatch({
                    type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                    payload: {
                        submissionPeriodStartDate: incrementStartDate,
                    },
                });
            }
        };
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        hasCreationAccess = profile.isCreationAllowed;
        hasManagementAccess = profile.isManagementAllowed;
        hasViewAccess = profile.isViewAllowed;
    }

    const submissionPeriodStartDateRef = useRef(profile?.schedule.submissionPeriodStartDate);

    useEffect(() => {
        submissionPeriodStartDateRef.current = profile?.schedule.submissionPeriodStartDate;
    }, [profile?.schedule.submissionPeriodStartDate]);

    useEffect(() => {
        if (profile && isDraft) {
            handleIncrementSubmissionStartDate();
        }

        const interval = setInterval(() => {
            const submissionPeriodStartDate = submissionPeriodStartDateRef.current;
            const isInFuture = submissionPeriodStartDate
            && (applyUkgUserTimeZoneTransformation(submissionPeriodStartDate) - moment() > 0);
            if (profile.status === BidProfileStatus.Draft && !isInFuture) {
                handleIncrementSubmissionStartDate();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (profile) {
            if (profile.originalBidProfileInformation.scheduleSource === ScheduleSource.WorkplanManager
                || profile.originalBidProfileInformation.scheduleSource == null) {
                dispatch(loadWorkplans(profile.id, profile.originalScheduleTemplates.isActiveWorkplanBid));
            }
        }
    }, [profile.originalBidProfileInformation.scheduleSource]);

    return (
        <>
            <BidEditHeader bidProfile={profile} />
            <TabContext value={selectedTab.toString()}>
                <Paper
                    className="manager--bid-edit-tabs-container"
                    square
                >
                    <Tabs
                        value={selectedTab}
                        onChange={(event, newTab) => setSelectedTab(newTab)}
                        indicatorColor="primary"
                        textColor="primary"
                        aria-label="bid edit tabs"
                    >
                        <Tab
                            label={t('tabs.configuration')}
                            disabled={!(hasCreationAccess || hasViewAccess)}
                            {...a11yProps(BidProfileEditTab.Form)}
                        />
                        <Tab
                            label={t('tabs.summary')}
                            {...a11yProps(BidProfileEditTab.Summary)}
                        />
                        <Tab
                            label={t('tabs.bidDashboard')}
                            disabled={!(hasManagementAccess || hasViewAccess)}
                            {...a11yProps(BidProfileEditTab.Dashboard)}
                        />
                    </Tabs>
                </Paper>
                <div className="bid-edit-full-page">
                    <TabPanel value={BidProfileEditTab.Form.toString()}>
                        <div className="configuration-container">
                            <BaseInfo />
                            <ScheduleSelection />
                            <EmployeeSelection />
                            <SchedulePeriod />
                        </div>
                    </TabPanel>
                    <TabPanel value={BidProfileEditTab.Summary.toString()}>
                        <BidSummary />
                    </TabPanel>
                    <TabPanel
                        value={BidProfileEditTab.Dashboard.toString()}
                        className="bid-dashboard-tab-panel"
                    >
                        <BidDashboard />
                    </TabPanel>
                </div>
            </TabContext>
        </>
    );
}

export default BidProfileEdit;
